import type { FC, PointerEvent, ReactNode } from 'react'
import type { PackageLabel } from '@/components/three/package-data'
import type { PackageControl } from '@/components/three/package-scene'

import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { WebGLBoundary, supportsWebGL } from '@/components/three/webgl-boundary'
import { packageKindFor } from '@/components/three/package-data'

interface ProductPackageProps {
    label: PackageLabel
    fallback: ReactNode
    className?: string
}

const PackageGlow: FC = (): ReactNode => {
    return (
        <div aria-hidden='true' className='absolute inset-0 flex items-center justify-center'>
            <div className='size-1/2 animate-pulse rounded-full bg-gold/15 blur-3xl' />
        </div>
    )
}

const PackageScene = dynamic(() => import('@/components/three/package-scene'), {
    ssr: false,
    loading: () => <PackageGlow />
})

/* Rotating 3D package for one product. Drag sideways to turn it; a new product spins it a full turn. */
export const ProductPackage: FC<ProductPackageProps> = ({ label, fallback, className }): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const control = useRef<PackageControl>({ dragging: false, lastX: 0, offset: 0, velocity: 0, burst: 0 })
    const firstLabel = useRef(true)
    const inView = useInView(containerRef)
    const reducedMotion = useReducedMotion()
    const [webgl, setWebgl] = useState<boolean | null>(null)
    const kind = packageKindFor(label.categorySlug)

    useEffect(() => {
        setWebgl(supportsWebGL())
    }, [])

    useEffect(() => {
        if (firstLabel.current) {
            firstLabel.current = false
            return
        }

        if (!reducedMotion) control.current.burst += Math.PI * 2
    }, [label.name, reducedMotion])

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        const drag = control.current

        drag.dragging = true
        drag.lastX = event.clientX
        drag.velocity = 0
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const drag = control.current

        if (!drag.dragging) return

        const delta = event.clientX - drag.lastX

        drag.lastX = event.clientX
        drag.offset += delta * 0.012
        drag.velocity = delta * 0.012
    }

    const onPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
        control.current.dragging = false

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const interactive = webgl === true

    return (
        <div
            ref={containerRef}
            role='img'
            aria-label={`بسته‌بندی سه‌بعدی ${label.name}`}
            onPointerDown={interactive ? onPointerDown : undefined}
            onPointerMove={interactive ? onPointerMove : undefined}
            onPointerUp={interactive ? onPointerEnd : undefined}
            onPointerCancel={interactive ? onPointerEnd : undefined}
            style={{ touchAction: 'pan-y' }}
            className={cn('relative size-full select-none', interactive && 'cursor-grab active:cursor-grabbing', className)}
        >
            {webgl === null && <PackageGlow />}
            {webgl === false && fallback}

            {interactive && (
                <WebGLBoundary fallback={fallback}>
                    <PackageScene label={label} kind={kind} control={control} active={inView} animate={!reducedMotion} />
                </WebGLBoundary>
            )}
        </div>
    )
}
